<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { onKeyStroke } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
});

const show = defineModel('show', { type: Boolean, default: false });
const { t } = useI18n();

const hasError = ref(false);
const dialogRef = ref(null);

watch(
  () => props.src,
  () => {
    hasError.value = false;
  }
);

watch(show, async visible => {
  if (!visible) return;
  hasError.value = false;
  await nextTick();
  dialogRef.value?.focus?.();
});

const close = () => {
  show.value = false;
};

onKeyStroke('Escape', e => {
  if (!show.value) return;
  close();
  e.preventDefault();
  e.stopPropagation();
});

const onImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      ref="dialogRef"
      role="dialog"
      aria-modal="true"
      :aria-label="name || t('CONVERSATION.HEADER.VIEW_PHOTO')"
      tabindex="-1"
      class="fixed inset-0 z-[9990] flex flex-col items-center justify-center gap-4 p-6 bg-n-alpha-black2 backdrop-blur-[4px] outline-none"
      @click.self="close"
    >
      <Button
        ghost
        slate
        icon="i-lucide-x"
        class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
        :aria-label="t('CONVERSATION.HEADER.CLOSE')"
        @click="close"
      />
      <img
        v-if="src && !hasError"
        :src="src"
        :alt="name"
        decoding="async"
        class="max-h-[80vh] max-w-[min(90vw,40rem)] object-contain rounded-2xl shadow-lg select-none"
        @click.stop
        @error="onImageError"
      />
      <p v-else class="m-0 text-sm text-n-slate-11 text-center">
        {{ t('CONVERSATION.HEADER.PHOTO_UNAVAILABLE') }}
      </p>
      <p
        v-if="name"
        class="m-0 text-base font-medium text-n-slate-12 text-center"
      >
        {{ name }}
      </p>
    </div>
  </Teleport>
</template>
